export function getBrowser(): Browser {
    return Browser.GetBrowser();
}

function _endsWith(value: string, search: string) {
    const len = value.length;
    const start = len - search.length;
    return value.substring(start >= 0 ? start : 0, len) === search;
}

/**
 * Gekopieerd van https://github.com/microsoft/ApplicationInsights-JS/blob/692f6fb507e6040209880d3fb9277c227918e5bb/legacy/JavaScript/JavaScriptSDK/Util.ts
 * @param userAgent De useragent (browserstring)
 */
export function disallowsSameSiteNone(userAgent: string) {
    if (typeof userAgent !== "string") {
        return false;
    }

    // Cover all iOS based browsers here. This includes:
    // - Safari on iOS 12 for iPhone, iPod Touch, iPad
    // - WkWebview on iOS 12 for iPhone, iPod Touch, iPad
    // - Chrome on iOS 12 for iPhone, iPod Touch, iPad
    // All of which are broken by SameSite=None, because they use the iOS networking stack
    if (userAgent.indexOf("CPU iPhone OS 12") !== -1 || userAgent.indexOf("iPad; CPU OS 12") !== -1) {
        return true;
    }

    // Cover Mac OS X based browsers that use the Mac OS networking stack. This includes:
    // - Safari on Mac OS X
    // This does not include:
    // - Internal browser on Mac OS X
    // - Chrome on Mac OS X
    // - Chromium on Mac OS X
    // Because they do not use the Mac OS networking stack.
    if (userAgent.indexOf("Macintosh; Intel Mac OS X 10_14") !== -1 && userAgent.indexOf("Version/") !== -1 && userAgent.indexOf("Safari") !== -1) {
        return true;
    }

    // Cover Mac OS X internal browsers that use the Mac OS networking stack. This includes:
    // - Internal browser on Mac OS X
    // This does not include:
    // - Safari on Mac OS X
    // - Chrome on Mac OS X
    // - Chromium on Mac OS X
    // Because they do not use the Mac OS networking stack.
    if (userAgent.indexOf("Macintosh; Intel Mac OS X 10_14") !== -1 && _endsWith(userAgent, "AppleWebKit/605.1.15 (KHTML, like Gecko)")) {
        return true;
    }

    // Cover Chrome 50-69, because some versions are broken by SameSite=None, and none in this range require it.
    // Note: this covers some pre-Chromium Edge versions, but pre-Chromim Edge does not require SameSite=None, so this is fine.
    // Note: this regex applies to Windows, Mac OS X, and Linux, deliberately.
    if (userAgent.indexOf("Chrome/5") !== -1 || userAgent.indexOf("Chrome/6") !== -1) {
        return true;
    }

    // Unreal Engine runs Chromium 59, but does not advertise as Chrome until 4.23. Treat versions of Unreal
    // that don't specify their Chrome version as lacking support for SameSite=None.
    if (userAgent.indexOf("UnrealEngine") !== -1 && userAgent.indexOf("Chrome") === -1) {
        return true;
    }

    // UCBrowser < 12.13.2 ignores Set-Cookie headers with SameSite=None
    // NB: this rule isn't complete - you need regex to make a complete rule.
    // See: https://www.chromium.org/updates/same-site/incompatible-clients
    if (userAgent.indexOf("UCBrowser/12") !== -1 || userAgent.indexOf("UCBrowser/11") !== -1) {
        return true;
    }

    return false;
}

export function areCookiesEnabled(inIFrame = false, navigator: Navigator = window.navigator): boolean {
    let cookieEnabled = navigator.cookieEnabled;

    if (typeof cookieEnabled === "undefined" || cookieEnabled) {
        const cookiename = "testcookie" + (new Date()).getTime();
        const sameSite = disallowsSameSiteNone(navigator.userAgent) ? "" : ";SameSite=none";
        const attributes = inIFrame ? sameSite + ";secure" : "";
        document.cookie = cookiename + "=" + attributes;
        cookieEnabled = document.cookie.indexOf(cookiename) !== -1;
        if (cookieEnabled) {
            //historische expiration datum zetten zodat testcookie ook meteen weer wordt verwijderd door browser
            document.cookie = cookiename + "=" + attributes + ";expires=Thu, 01 Jan 1970 00:00:00 GMT;path=";
        }
    }

    return !!cookieEnabled;
}

/**
* Sets a cookie value and expiry date on a specific cookie by name.
* 
* @param cookieName The name of the cookie.
* @param cookieValue The value of the cookie.
* @param expiryDays Days untill the cookie will expire.
* @param restrictToPage Whether the path should be set to the current page or the root
*/
export function setCookieValue(cookieName: string, cookieValue: string, expiryDays: number, restrictToPage = true): void {
    const date = new Date();
    date.setTime(date.getTime() + expiryDays * 24 * 60 * 60 * 1000);
    const expires = "; expires=" + date.toUTCString();
    const path = restrictToPage ? "" : "; path=/";
    document.cookie = cookieName + "=" + cookieValue + expires + path;
}

/**
* Gets a cookie value by name
* 
* @param cookieName - The name of the cookie
* @returns The cookie value if found, else ""
*/
export function getCookieValue(cookieName: string): string {
    const name = cookieName + "=";
    const cookies = document.cookie.split(';');

    for (let i = 0; i < cookies.length; i++) {
        const cookie = cookies[i].trim();
        if (cookie.indexOf(name) === 0) {
            return cookie.substring(name.length, cookie.length);
        }
    }

    return "";
}

/**
* @remarks
* return true als:
*    - de browser niet Safari is, of
*    - wel Safari is, maar safariWorkaround=stap2 in de querystring in het huidige adres zit, of
*    - wel Safari is, maar deze (toch nog) een oude cookie checkCookieEnabled heeft meegestuurd
*       (dit kan gebeuren als cookieblocking weer terug is aangezet, maar Safari nog niet gesloten was).
*/
export function checkForSafariCookieWorkaround(navigator: Navigator = window.navigator, location: Location = window.location): boolean {
    return navigator.userAgent.indexOf("Safari") === -1
        || location.search.indexOf("safariWorkaround=stap2") > -1
        || document.cookie.indexOf("checkCookieEnabled") !== -1;
}

// https://stackoverflow.com/questions/326069/how-to-identify-if-a-webpage-is-being-loaded-inside-an-iframe-or-directly-into-t
export function isInIframe(): boolean {
    try {
        return window.self !== window.top;
    } catch (e) {
        return true;
    }
}

/** 
* Deze functie bepaalt of de browser toLocaleString ondersteunt.
* source https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Number/toLocaleString#Checking_for_support_for_locales_and_options_arguments
* @returns true of false of de browser de functie ondersteunt.
*/
export function toLocaleStringSupportsOptions(): boolean {
    return !!(typeof Intl === "object" && Intl && typeof Intl.NumberFormat === "function");
}

/** 
* Deze functie bestaat als wrapper zodat we het platform kunnen mocken in automatische tests
* @returns het platform als string of een lege string als er geen platform is in de navigator 
*/
export function GetNavigatorPlatform(): string {
    let platform = "";
    if (navigator.platform) {
        platform = navigator.platform;
    }
    return platform;
}

/**
* Deze functie bestaat als wrapper zodat we in automatische tests kunnen mocken of de browser https is
* @returns true als de browser op https zit, anders false.
*/
export function IsConnectionSecure() {
    return document.location.protocol.toLowerCase() === 'https:';
}

export class Browser {
    // cache the browser so we only have to determine it once
    private static Browser: Browser | null = null;

    constructor(private type: Browsers) { }

    private static DetermineBrowser(): void {
        let type: Browsers = Browsers.Other;

        // based on https://stackoverflow.com/a/53636541 and modified to comply to our compiler options
        // changes a lot for aanp://20190201421 because we found out big parts of the function did not work
        if ((!!(<any>window)["opr"] && !!(<any>["opr"])["addons"]) || !!(<any>window)["opera"] || navigator.userAgent.indexOf(' OPR/') >= 0) {
            // Opera 8.0+
            type = Browsers.Opera;
        } else if (typeof (<any>window)["InstallTrigger"] !== 'undefined') {
            // Firefox 1.0+
            type = Browsers.Firefox;
        } else if (/^((?!chrome|android|CriOS).)*safari/i.test(navigator.userAgent) || /constructor/i.test((<any>window)["HTMLElement"]) || (function (p) { return p.toString() === "[object SafariRemoteNotification]"; })(!(<any>window)['safari'] || (typeof (<any>window)["safari"] !== 'undefined' && (<any>window)["safari"].pushNotification))) {
            // Safari 3.0+ "[object HTMLElementConstructor]"
            type = Browsers.Safari;
        } else if (window.navigator.userAgent.indexOf("MSIE ") > -1 || !!window.navigator.userAgent.match(/Trident.*rv\:11\./) || !!(<any>document)["documentMode"]) {
            // Internet Explorer 6-11
            type = Browsers.InternetExplorer;
        } else if (!(<any>document)["documentMode"] && !!(<any>window)["StyleMedia"]) {
            // Edge 20+
            type = Browsers.Edge;
        } else if (navigator.userAgent.match(/SamsungBrowser/i)) {
            // Prevent samsung stock browsers to be confused with chrome.
            // type is other by default so do nothing
        } else if (((<any>window)["chrome"] !== null && typeof (<any>window)["chrome"] !== 'undefined' && window.navigator.vendor === 'Google Inc.') || window.navigator.userAgent.match("CriOS")) {
            // Chrome 1+
            type = Browsers.Chrome;
        }

        this.Browser = new Browser(type);
    }

    public isOpera = (): boolean => {
        return this.type === Browsers.Opera;
    }

    public isFirefox = (): boolean => {
        return this.type === Browsers.Firefox;
    }

    public isSafari = (): boolean => {
        return this.type === Browsers.Safari;
    }

    public isChrome = (): boolean => {
        return this.type === Browsers.Chrome;
    }

    public isIE = (): boolean => {
        return this.type === Browsers.InternetExplorer;
    }

    public isEdge = (): boolean => {
        return this.type === Browsers.Edge;
    }

    public isOther = (): boolean => {
        return this.type === Browsers.Other;
    }

    public static GetBrowser(): Browser {
        if (Browser.Browser === null) {
            this.DetermineBrowser();
        }

        // the browser will always get determined in the DetermineBrowser() function so we can be sure its instanced here.
        return <Browser>this.Browser;
    }

    public getIosVersion = (): number => {
        const agent = navigator.userAgent;
        const start = agent.indexOf('OS ');
        if (this.isSafari() && start > -1) {
            return parseFloat(agent.substr(start + 3, 4).trim().replace('_', '.'));
        }
        return 0;
    }
}

export enum Browsers {
    Opera,
    Firefox,
    Safari,
    Chrome,
    InternetExplorer,
    Edge,
    Other
}
